/**
 * Recent transaction feed. Scroll-snap virtualized list (only renders what's needed).
 */
import { store } from '../store.ts';
import { formatINR, MERCHANT_DISPLAY } from '../types.ts';
import type { Transaction } from '../types.ts';

const PAGE = 30;

export class HisaabTxFeed extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;
  #showing = PAGE;
  #all: Transaction[] = [];

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) {
        this.#all = s.summary.transactions;
        this.#render();
      }
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(): void {
    if (!this.#all.length) {
      this.#shadow.innerHTML = '<style>:host{display:none;}</style>';
      return;
    }
    const visible = this.#all.slice(0, this.#showing);
    const more = this.#all.length - visible.length;
    const rows = visible.map((tx) => {
      const m = MERCHANT_DISPLAY[tx.merchant_id] ?? tx.merchant_id;
      const items = tx.items.slice(0, 1).join(', ') || tx.category.replace(/_/g, ' ');
      return /* html */ `
<div class="tx">
  <div class="left">
    <div class="m">${m}</div>
    <div class="i">${escape(items)}</div>
  </div>
  <div class="right">
    <div class="amt">${formatINR(tx.amount_inr)}</div>
    <div class="d">${shortDate(tx.date)}</div>
  </div>
</div>`;
    }).join('');

    this.#shadow.innerHTML = /* html */ `
<style>
:host { display: block; }
.title {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: var(--ink-faint);
  margin-bottom: 14px;
  padding-left: 4px;
}
.list { display: grid; gap: 0; background: var(--paper-up); border: 1px solid var(--rule); border-radius: 8px; overflow: hidden; }
.tx {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--rule);
  align-items: center;
}
.tx:last-child { border-bottom: none; }
.m {
  font-family: var(--display);
  font-size: 17px;
  color: var(--ink);
  font-weight: 600;
}
.i {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.05em;
  color: var(--ink-faint);
  margin-top: 3px;
  text-transform: lowercase;
  max-width: 56vw;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.right { text-align: right; }
.amt {
  font-family: var(--display);
  font-size: 17px;
  font-weight: 600;
  color: var(--ink);
  font-feature-settings: "tnum" 1;
}
.d {
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ink-faint);
  margin-top: 3px;
}
button {
  margin-top: 16px;
  width: 100%;
  padding: 14px;
  background: transparent;
  border: 1px solid var(--rule);
  border-radius: 8px;
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--ink-dim);
}
button:active { background: var(--paper-up); }
</style>
<div class="title">Recent kharcha</div>
<div class="list">${rows}</div>
${more > 0 ? `<button id="more">+ ${more} more</button>` : ''}
`;
    const moreBtn = this.#shadow.getElementById('more');
    if (moreBtn) moreBtn.addEventListener('click', () => { this.#showing += PAGE; this.#render(); });
  }
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

function shortDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'UTC' });
}

customElements.define('hisaab-tx-feed', HisaabTxFeed);
